import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useAuth } from "./AuthContext";
import paymentService from "../services/payment.service";
import { InitPaymentResponse, PaymentDetailsResponse, InitiatePaymentRequest, FetchAllPaymentsQueryParams, AddJobSeekerDetailsRequest, Attachments } from "../types/api";
import userService from "../services/user.service";
import { GetJobSeekerDetailsResponse } from "../types/api";
import { JobSeekerContextType } from "../types/context";

const JobSeekerContext = createContext<JobSeekerContextType | undefined>(undefined);

export const JobSeekerProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [payments, setPayments] = useState<PaymentDetailsResponse[]>([]);
  const [isOnboardingPaymentDone, setIsOnboardingPaymentDone] = useState<boolean>(false);
  const [paymentLoading, setPaymentLoading] = useState<boolean>(true);
  const [jobSeekerDetails, setJobSeekerDetails] = useState<GetJobSeekerDetailsResponse | null>(null);
  const [isProfileComplete, setIsProfileComplete] = useState<boolean>(false);
  const [incompleteStep, setIncompleteStep] = useState<number>(0); // 0-indexed step number

  interface LoadingState {
    fetchData: boolean;
    startPayment: boolean;
    fetchPaymentDetails: boolean;
  }

  const [loadingState, setLoadingState] = useState<LoadingState>({
    fetchData: true,
    startPayment: false,
    fetchPaymentDetails: false,
  });

  const setLoading = (key: keyof LoadingState, value: boolean) => {
    setLoadingState((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    const fetchData = async () => {
      if (user && (user.role === "Jobseeker" || user.role === "Intern")) {
        setLoading("fetchData", true);
        setError(null);
        try {
          // TODO add Jobseeker if payment block is required , else remove payment flow.
          if (user.role === "Intern" || user.role === "Jobseeker") {
            setIsOnboardingPaymentDone(true);
            setPaymentLoading(false);
          } 
          // else {
          //   // Fetch payments
          //   const params: FetchAllPaymentsQueryParams = { userId: user.userId };
          //   setPaymentLoading(true);
          //   const fetchedPayments = await paymentService.fetchAllPayments(params);
          //   setPayments(fetchedPayments);

          //   const onboardingPaid = fetchedPayments.some(
          //     (payment) =>
          //       payment.purpose === "JobSeeker_Onboarding" && payment.paymentStatus === "Paid"
          //   );
          //   setIsOnboardingPaymentDone(onboardingPaid);
          //   setPaymentLoading(false);
          // }

          // Fetch job seeker details
          const jobSeekerResponse = await userService.getJobSeekerDetails(user.userId);
          if (jobSeekerResponse && jobSeekerResponse.data) {
            setJobSeekerDetails(jobSeekerResponse.data);
            
            setIsProfileComplete(jobSeekerResponse?.data?.isProfileComplete || false);
            setIncompleteStep(0);

          } else {
            setIsProfileComplete(false);
            setIncompleteStep(0); // Default to first step if no data
          }
        } catch (err: any) {
          console.error("Error fetching job seeker data:", err);
          setError(err.message || "Failed to fetch job seeker data.");
          setIsProfileComplete(false);
          setIncompleteStep(0);
        } finally {
          setLoading("fetchData", false);
        }
      }
    };
    fetchData();
  }, [user?.userId, user?.role]);

  const startOnboardingPayment = async (paymentId: string): Promise<{payload:InitPaymentResponse | undefined, successCallback : () => void}> => {
    setLoading("startPayment", true);
    setError(null);
    try {
      if (!user || !user.userId) {
        throw new Error("User not authenticated.");
      }

      const paymentRequest: InitiatePaymentRequest = {
        purpose: "JobSeeker_Onboarding",
        paymentType: "Online",
        userId: user.userId,
        paymentId: paymentId,
      };

      const response: InitPaymentResponse = await paymentService.initiatePayment(paymentRequest);
      console.log("Onboarding payment initiated:", response);
      return {payload : response , successCallback : () => {
        setIsOnboardingPaymentDone(true)
      }};
    } catch (err: any) {
      console.error("Error initiating onboarding payment:", err);
      setError(err.message || "Failed to initiate onboarding payment.");
      return {payload : undefined , successCallback : () => {}};
    } finally {
      setLoading("startPayment", false);
    }
  };

  const getJobSeekerPaymentDetails = async (paymentId: string): Promise<PaymentDetailsResponse | undefined> => {
      setLoading("fetchPaymentDetails", true);
    setError(null);
    try {
      const response: PaymentDetailsResponse = await paymentService.getPaymentDetails(paymentId);
      return response;
    } catch (err: any) {
      console.error("Error fetching payment details:", err);
      setError(err.message || "Failed to fetch payment details.");
      return undefined;
    } finally {
      setLoading("fetchPaymentDetails", false);
    }
  };

  const addJobSeekerDetails = async (details: AddJobSeekerDetailsRequest): Promise<void> => {
    // setLoading("fetchData", true); // Use fetchData loading for this as it updates overall details
    setError(null);
    setIncompleteStep(-1) // disables auto redirect post user interaction
    try {
      if (!user || !user.userId) {
        throw new Error("User not authenticated.");
      }
      const response = await userService.addJobSeekerDetails(details);
      // Re-fetch job seeker details to update context state
      const jobSeekerResponse = await userService.getJobSeekerDetails(user.userId);
        if (jobSeekerResponse && jobSeekerResponse.data) {
          setJobSeekerDetails(jobSeekerResponse.data);
          setIsProfileComplete(jobSeekerResponse?.data?.isProfileComplete || false);
        }
    } catch (err: any) {
      console.error("Error adding job seeker details:", err);
      setError(err.message || "Failed to add job seeker details.");
      throw error;
    } finally {
      // setLoading("fetchData", false);
    }
  };

  const updateJobSeekerAttachments = (newAttachment: Attachments) => {
      setJobSeekerDetails((prevDetails) => {
        if (!prevDetails) return null;
  
        const existingAttachments = prevDetails.attachments || [];
        const updatedAttachments = existingAttachments
          .filter((att) => att.typeId !== newAttachment.typeId) // Remove old attachment of the same type
          .concat(newAttachment); // Add the new attachment
  
        return {
          ...prevDetails,
          attachments: updatedAttachments,
        };
      });
    };
  return (
    <JobSeekerContext.Provider
      value={{
        startOnboardingPayment,
        getJobSeekerPaymentDetails,
        loadingState,
        error,
        payments,
        isOnboardingPaymentDone,
        paymentLoading,
        jobSeekerDetails,
        isProfileComplete,
        incompleteStep,
        addJobSeekerDetails,
        updateJobSeekerAttachments
      }}
    >
      {children}
    </JobSeekerContext.Provider>
  );
};

export const useJobSeeker = () => {
  const context = useContext(JobSeekerContext);
  if (context === undefined) {
    throw new Error("useJobSeeker must be used within a JobSeekerProvider");
  }
  return context;
};
